import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";

export type ClientSettingField =
	| "vat_display_override"
	| "patient_name_format"
	| "is_inv_w_job_title"
	| "is_inv_w_pat_data"
	| "srg_sorting_option"
	| "is_srg_grouped_by_prac"
	| "is_show_insurance_on_pdf";

interface ClientSettingsFilterProps {
	selectedFields: ClientSettingField[];
	onFieldsChange: (fields: ClientSettingField[]) => void;
}

const FIELD_LABELS: Record<ClientSettingField, string> = {
	vat_display_override: "MwSt. Anzeige Override",
	patient_name_format: "Schreibweise Patientenname auf Sammelrechnungen",
	is_inv_w_job_title: "Rechnung mit Auftragstitel",
	is_inv_w_pat_data: "Rechnung mit Patientendaten",
	srg_sorting_option: "Sammelrechnung Sortierung",
	is_srg_grouped_by_prac: "Sammelrechnung nach Behandler gruppiert",
	is_show_insurance_on_pdf: "Versicherungsinformationen auf KV/LS anzeigen",
};

export const ALL_FIELDS: ClientSettingField[] = [
	"vat_display_override",
	"is_inv_w_job_title",
	"is_inv_w_pat_data",
	"patient_name_format",
	"srg_sorting_option",
	"is_srg_grouped_by_prac",
	"is_show_insurance_on_pdf",
];

export const ClientSettingsFilter: React.FC<ClientSettingsFilterProps> = ({
	selectedFields,
	onFieldsChange,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const handleToggleField = (field: ClientSettingField) => {
		if (selectedFields.includes(field)) {
			onFieldsChange(selectedFields.filter(f => f !== field));
		} else {
			onFieldsChange([...selectedFields, field]);
		}
	};

	const handleSelectAll = () => {
		onFieldsChange(ALL_FIELDS);
	};

	const handleDeselectAll = () => {
		onFieldsChange([]);
	};

	return (
		<div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden">
			<div
				className="p-4 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors flex items-center justify-between"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div>
					<h3 className="font-semibold text-gray-900">
						Kunden-Einstellungsfilter
					</h3>
					<p className="text-sm text-gray-600">
						{selectedFields.length > 0
							? `${selectedFields.length} Feld${selectedFields.length !== 1 ? 'er' : ''} ausgewählt`
							: 'Keine Felder ausgewählt (nur erster Kunde pro Labor)'}
					</p>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={(e) => {
						e.stopPropagation();
						setIsExpanded(!isExpanded);
					}}
					aria-label={isExpanded ? "Zuklappen" : "Aufklappen"}
				>
					{isExpanded ? (
						<ChevronUp className="w-5 h-5" />
					) : (
						<ChevronDown className="w-5 h-5" />
					)}
				</Button>
			</div>

			{isExpanded && (
				<div className="p-4 space-y-4 bg-gray-50">
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={handleSelectAll}
							disabled={selectedFields.length === ALL_FIELDS.length}
						>
							Alle auswählen
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={handleDeselectAll}
							disabled={selectedFields.length === 0}
						>
							Alle abwählen
						</Button>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{ALL_FIELDS.map((field) => (
							<div
								key={field}
								className="flex items-center space-x-2 p-2 rounded hover:bg-white transition-colors"
							>
								<Checkbox
									id={`field-${field}`}
									checked={selectedFields.includes(field)}
									onCheckedChange={() => handleToggleField(field)}
								/>
								<Label
									htmlFor={`field-${field}`}
									className="cursor-pointer text-sm"
								>
									{FIELD_LABELS[field]}
								</Label>
							</div>
						))}
					</div>

					<div className="pt-2 border-t border-gray-300">
						<p className="text-xs text-gray-600">
							Pro Labor werden nur Kunden mit unterschiedlichen Kombinationen der ausgewählten Felder berücksichtigt.
							{selectedFields.length === 0 && " Aktuell wird nur der erste Kunde pro Labor verwendet."}
						</p>
					</div>
				</div>
			)}
		</div>
	);
};
